import { Box, Flex, Icon, Tag, TagCloseButton, TagLabel, Text, Button, useColorModeValue } from '@chakra-ui/react';
import { keyframes as emotionKeyframes } from '@emotion/react';
import { IconType } from 'react-icons';
import { MdFilterList, MdInbox, MdSearch } from 'react-icons/md';
import FadeIn from 'components/animations/FadeIn';
import { ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Breathing ghost animation for first-use empty states
// ---------------------------------------------------------------------------
const breathe = emotionKeyframes`
	0%, 100% { opacity: 0.3; }
	50% { opacity: 0.6; }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EmptyStateVariant = 'default' | 'first-use' | 'no-results-search' | 'no-results-filter' | 'completion';

interface EmptyStateBaseProps {
	icon?: IconType;
	/** Custom illustration component — replaces the icon circle when provided */
	illustration?: ReactNode;
	title: string;
	description?: string;
	actionLabel?: string;
	onAction?: () => void;
}

interface DefaultVariantProps extends EmptyStateBaseProps {
	variant?: 'default';
}

interface FirstUseVariantProps extends EmptyStateBaseProps {
	variant: 'first-use';
}

interface SearchVariantProps extends EmptyStateBaseProps {
	variant: 'no-results-search';
	/** The search query to echo back */
	searchQuery?: string;
	/** Suggested searches to show as clickable links */
	suggestions?: string[];
	onSuggestionClick?: (suggestion: string) => void;
}

interface FilterVariantProps extends EmptyStateBaseProps {
	variant: 'no-results-filter';
	/** Active filter labels to show as removable chips */
	activeFilters?: string[];
	onRemoveFilter?: (filter: string) => void;
	onClearAllFilters?: () => void;
}

interface CompletionVariantProps extends EmptyStateBaseProps {
	variant: 'completion';
}

export type EmptyStateProps =
	| DefaultVariantProps
	| FirstUseVariantProps
	| SearchVariantProps
	| FilterVariantProps
	| CompletionVariantProps;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EmptyState(props: EmptyStateProps) {
	const {
		icon = MdInbox,
		illustration,
		title,
		description,
		actionLabel,
		onAction,
	} = props;

	const variant = props.variant ?? 'default';

	const iconColor = useColorModeValue('secondaryGray.500', 'secondaryGray.600');
	const titleColor = useColorModeValue('secondaryGray.900', 'white');
	const descColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
	const iconBg = useColorModeValue('gray.50', 'navy.700');
	const dangerColor = useColorModeValue('danger.500', 'danger.500');
	const brandColor = useColorModeValue('brand.500', 'brand.400');
	const linkColor = useColorModeValue('brand.500', 'brand.300');

	// Variant-specific rendering
	const isFirstUse = variant === 'first-use';
	const isSearch = variant === 'no-results-search';
	const isFilter = variant === 'no-results-filter';
	const isCompletion = variant === 'completion';

	// Title font: Instrument Serif for first-use and completion
	const titleFont = (isFirstUse || isCompletion)
		? 'var(--display-font, "Urbanist", "Figtree", sans-serif)'
		: '"Urbanist", "Figtree", sans-serif';
	const titleSize = (isFirstUse || isCompletion) ? '20px' : 'lg';
	const titleWeight = (isFirstUse || isCompletion) ? '400' : '700';
	const titleStyle = (isFirstUse || isCompletion) ? 'italic' : 'normal';

	// Override icon for search/filter variants
	const effectiveIcon = isSearch ? MdSearch : isFilter ? MdFilterList : icon;

	// Completion gets a warm radial glow
	const completionBg = useColorModeValue(
		`radial-gradient(circle at center, rgba(2, 63, 127, 0.02) 0%, transparent 70%)`,
		`radial-gradient(circle at center, rgba(53, 120, 191, 0.03) 0%, transparent 70%)`
	);

	return (
		<FadeIn>
			<Flex
				direction='column'
				align='center'
				justify='center'
				py='60px'
				px='20px'
				{...(isCompletion ? { background: completionBg } : {})}
			>
				{/* Icon / Illustration */}
				{illustration ? (
					<Flex
						mb='24px'
						{...(isFirstUse ? { animation: `${breathe} 3s ease-in-out infinite` } : {})}
					>
						{illustration}
					</Flex>
				) : !isCompletion ? (
					<Flex
						align='center'
						justify='center'
						w={isSearch || isFilter ? '48px' : '80px'}
						h={isSearch || isFilter ? '48px' : '80px'}
						borderRadius='50%'
						bg={isSearch || isFilter ? 'transparent' : iconBg}
						mb='20px'
						{...(isFirstUse ? { animation: `${breathe} 3s ease-in-out infinite` } : {})}
					>
						<Icon
							as={effectiveIcon}
							w={isSearch || isFilter ? '48px' : '36px'}
							h={isSearch || isFilter ? '48px' : '36px'}
							color={iconColor}
						/>
					</Flex>
				) : (
					/* Completion: small court emoji */
					<Text fontSize='48px' mb='16px' opacity={0.3}>
						🎾
					</Text>
				)}

				{/* Title */}
				<Text
					color={titleColor}
					fontSize={titleSize}
					fontWeight={titleWeight}
					fontStyle={titleStyle}
					fontFamily={titleFont}
					mb='8px'
					textAlign='center'
				>
					{isSearch && 'searchQuery' in props && props.searchQuery
						? `No matches for "${props.searchQuery}"`
						: title
					}
				</Text>

				{/* Description */}
				{description && !isCompletion && (
					<Text
						color={descColor}
						fontSize='sm'
						fontWeight='400'
						textAlign='center'
						maxW='360px'
						mb={actionLabel || isFilter ? '20px' : '0px'}
					>
						{description}
					</Text>
				)}

				{/* Filter variant: active filter chips */}
				{isFilter && 'activeFilters' in props && props.activeFilters && props.activeFilters.length > 0 && (
					<Flex wrap='wrap' gap='8px' justify='center' mb='16px' maxW='400px'>
						{props.activeFilters.map((filter) => (
							<Tag key={filter} size='sm' variant='subtle' colorScheme='brand'>
								<TagLabel>{filter}</TagLabel>
								{props.onRemoveFilter && (
									<TagCloseButton onClick={() => props.onRemoveFilter!(filter)} />
								)}
							</Tag>
						))}
					</Flex>
				)}
				{isFilter && 'onClearAllFilters' in props && props.onClearAllFilters && (
					<Text
						as='button'
						color={linkColor}
						fontSize='sm'
						fontWeight='500'
						onClick={props.onClearAllFilters}
						cursor='pointer'
						_hover={{ textDecoration: 'underline' }}
						mb='8px'
					>
						Clear all filters
					</Text>
				)}

				{/* Search variant: suggestions */}
				{isSearch && 'suggestions' in props && props.suggestions && props.suggestions.length > 0 && (
					<Flex direction='column' align='center' gap='4px' mt='8px'>
						{props.suggestions.map((s) => (
							<Text
								key={s}
								as='button'
								color={linkColor}
								fontSize='sm'
								cursor='pointer'
								_hover={{ textDecoration: 'underline' }}
								onClick={() => props.onSuggestionClick?.(s)}
							>
								{s}
							</Text>
						))}
					</Flex>
				)}

				{/* CTA button — not shown for search or completion */}
				{actionLabel && onAction && !isSearch && !isCompletion && (
					<Button variant='brand' size='md' onClick={onAction}>
						{actionLabel}
					</Button>
				)}
			</Flex>
		</FadeIn>
	);
}
